import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Button, Form, Input, Modal } from "antd";
import * as React from "react";
import { useCreateBlacklistBankAccount, useCreateBlacklistPhone } from "src/hooks";

export type BlacklistFormType = "phone" | "bankAccount";

export type BlacklistFormModalProps = {
  type: BlacklistFormType;
  onClose?: () => void;
};

type BlacklistFormValues = {
  value: string;
  reason: string;
};

const labelByType: Record<BlacklistFormType, string> = {
  phone: "เบอร์โทร",
  bankAccount: "ชื่อบัญชี",
};

const PHONE_MAX_LENGTH = 10; // matches backend constr(min_length=9, max_length=10, regex=^0[0-9]{8,9}$) — Thai landline (9) / mobile (10), leading 0 required
const REASON_MAX_LENGTH = 255; // matches backend REASON_MAX_LENGTH in schemas/v1/blacklist.py

export const BlacklistFormModal = NiceModal.create(
  ({ type, ...props }: BlacklistFormModalProps) => {
    const { visible, hide, remove } = useModal();
    const label = labelByType[type];
    const isPhone = type === "phone";

    const handleClose = () => {
      hide();
      props.onClose?.();
    };

    const [form] = Form.useForm<BlacklistFormValues>();

    const { mutate: createPhone, isLoading: isCreatingPhone } = useCreateBlacklistPhone();
    const { mutate: createBankAccount, isLoading: isCreatingBankAccount } =
      useCreateBlacklistBankAccount();

    const isSubmitting = isCreatingPhone || isCreatingBankAccount;

    const handleFinish = (values: BlacklistFormValues) => {
      const reason = values.reason.trim();

      if (type === "phone") {
        createPhone({ phone: values.value, reason }, { onSuccess: handleClose });
      } else {
        createBankAccount({ bank_account_name: values.value, reason }, { onSuccess: handleClose });
      }
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const digitsOnly = e.target.value.replace(/\D/g, "");
      form.setFieldsValue({ value: digitsOnly });
    };

    return (
      <Modal
        centered
        width={480}
        visible={visible}
        afterClose={remove}
        focusTriggerAfterClose={false}
        closable={false}
        title={`เพิ่ม${label}แบล็คลิส`}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px" }}>
            <Button key="cancel" size="large" onClick={handleClose} style={{ width: "100px" }}>
              ยกเลิก
            </Button>
            <Button
              key="submit"
              type="primary"
              size="large"
              loading={isSubmitting}
              onClick={() => form.submit()}
              style={{ width: "160px" }}
            >
              ยืนยัน
            </Button>
          </div>
        }
      >
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          requiredMark={true}
          preserve={false}
          onFinish={handleFinish}
        >
          <Form.Item
            label={label}
            name="value"
            required
            rules={
              isPhone
                ? [
                    { required: true, message: `โปรดระบุ${label}` },
                    {
                      pattern: /^0[0-9]{8,9}$/,
                      message: `${label}ต้องขึ้นต้นด้วย 0 และมีความยาว 9-10 หลัก`,
                    },
                  ]
                : [{ required: true, message: `โปรดระบุ${label}` }]
            }
          >
            <Input
              placeholder={`ระบุ${label}`}
              {...(isPhone
                ? { onChange: handlePhoneChange, maxLength: PHONE_MAX_LENGTH, inputMode: "numeric" }
                : {})}
            />
          </Form.Item>
          <Form.Item
            label="เหตุผล"
            name="reason"
            required
            rules={[
              { required: true, whitespace: true, message: "โปรดระบุเหตุผล" },
              { max: REASON_MAX_LENGTH, message: `เหตุผลต้องไม่เกิน ${REASON_MAX_LENGTH} ตัวอักษร` },
            ]}
          >
            <Input.TextArea
              placeholder="ระบุเหตุผล"
              rows={3}
              maxLength={REASON_MAX_LENGTH}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    );
  }
);

export default BlacklistFormModal;
